import { ambiguousNarrowSymbolCurrencies } from './currencySymbolsMap'

// Ported verbatim from qr-api's src/modules/utils/format-price.ts — per
// Payments team (Daniel, Slack 2026-08-11): CLDR price formatting is
// expected to be done frontend-side, not by PMS. `Intl.NumberFormat` has
// full CLDR data in both Node and the browser, so no separate CLDR library
// is needed (confirmed by Daniel).
//
// Takes an already-major-unit amount (e.g. 19, not 1900) — divide by 100
// (unless isExactPrice) before calling this, same split as qr-api itself
// (see mapPriceToProductVariablesDto in products-manager.service.ts).
const prefixPatternSpaceCache = new Map<string, boolean>()

function localeHasPrefixPatternSpace(locale: string): boolean {
	const cached = prefixPatternSpaceCache.get(locale)
	if (cached !== undefined) return cached

	const parts = new Intl.NumberFormat(locale, {
		style: 'currency',
		currency: 'USD',
		currencyDisplay: 'narrowSymbol',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}).formatToParts(1)

	const currencyIndex = parts.findIndex((p) => p.type === 'currency')
	const integerIndex = parts.findIndex((p) => p.type === 'integer')

	const result =
		currencyIndex !== -1 &&
		integerIndex !== -1 &&
		currencyIndex < integerIndex &&
		parts.some(
			(p, index) =>
				p.type === 'literal' &&
				index > currencyIndex &&
				index < integerIndex,
		)

	prefixPatternSpaceCache.set(locale, result)
	return result
}

export function formatPrice(
	amount: number,
	currency: string,
	locale = 'en',
): string {
	const resolvedLocale = locale || 'en'
	const resolvedCurrency = currency.toUpperCase()

	try {
		const currencyDisplay = ambiguousNarrowSymbolCurrencies.has(
			resolvedCurrency,
		)
			? 'symbol'
			: 'narrowSymbol'
		const isWholeNumber = Number.isInteger(amount)
		const formatter = new Intl.NumberFormat(resolvedLocale, {
			style: 'currency',
			currency: resolvedCurrency,
			currencyDisplay,
			...(isWholeNumber && { minimumFractionDigits: 0 }),
		})

		const parts = formatter.formatToParts(amount)
		const currencyIndex = parts.findIndex((p) => p.type === 'currency')
		const integerIndex = parts.findIndex((p) => p.type === 'integer')

		const hasPrefixSpacingLiteral =
			currencyIndex !== -1 &&
			integerIndex !== -1 &&
			currencyIndex < integerIndex &&
			parts.some(
				(p, index) =>
					p.type === 'literal' &&
					index > currencyIndex &&
					index < integerIndex,
			)

		if (
			hasPrefixSpacingLiteral &&
			!localeHasPrefixPatternSpace(resolvedLocale)
		) {
			return parts
				.filter(
					(p, index) =>
						!(
							p.type === 'literal' &&
							index > currencyIndex &&
							index < integerIndex
						),
				)
				.map((p) => p.value)
				.join('')
		}

		return parts.map((p) => p.value).join('')
	} catch {
		return new Intl.NumberFormat(resolvedLocale, {
			style: 'currency',
			currency: resolvedCurrency,
		}).format(amount)
	}
}
